// src/app/login/login.service.ts
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LoginstateService } from './loginstate.service';
import { User } from './types';
import { EncryptionService } from '../services/encryption.service';
import { getCookie, setCookie, deleteCookie } from './cookie-utils';
import { environment } from '../../environments/environment';
import { KEEP_SESSION_ON_401 } from './backend';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private endpoint = environment.backendUrl;

  // Cookie-Namen (für SSO)
  private COOKIE_JWT = 'MU_JWT';
  private COOKIE_EXP = 'MU_EXP';

  // LocalStorage-Key (nur fürs Login-Portal, kein SSO nötig)
  private LS_USER = '__auth_user__';

  public authentificationKey = environment.apiKey;

  constructor(
    private http: HttpClient,
    private loginStateService: LoginstateService,
    private encryptionService: EncryptionService
  ) {}

  // --- EXPIRATION ------------------------------------------------------------

  setExpirationToken(expiresAtMs: number): void {
    // Für SSO:
    setCookie(this.COOKIE_EXP, String(expiresAtMs), { expiresAtMs });
    // optional zusätzlich im LocalStorage (nur für das Portal selbst):
    localStorage.setItem(this.COOKIE_EXP, String(expiresAtMs));
  }

  getExpirationToken(): number {
    const fromCookie = getCookie(this.COOKIE_EXP);
    if (fromCookie) {
      return Number(fromCookie);
    }
    const fromLs = localStorage.getItem(this.COOKIE_EXP);
    return fromLs ? Number(fromLs) : 0;
  }

  removeExpirationToken(): void {
    deleteCookie(this.COOKIE_EXP);
    localStorage.removeItem(this.COOKIE_EXP);
  }

  // --- JWT -------------------------------------------------------------------

  setJWTToken(token: string, expiresAtMs?: number): void {
    console.log('[LoginService] setJWTToken', { tokenSnippet: token?.slice(0, 20), expiresAtMs });

    setCookie(this.COOKIE_JWT, token, {
      expiresAtMs: expiresAtMs ?? this.getExpirationToken() ?? undefined,
    });


    // optional zusätzlich, falls du im Portal selber noch LS brauchst:
    localStorage.setItem(this.COOKIE_JWT, token);
  }

  getJWTToken(): string | null {
    const fromCookie = getCookie(this.COOKIE_JWT);
    if (fromCookie) {
      return fromCookie;
    }
    const fromLs = localStorage.getItem(this.COOKIE_JWT);
    return fromLs ?? null;
  }

  removeJWTToken(): void {
    deleteCookie(this.COOKIE_JWT);
    localStorage.removeItem(this.COOKIE_JWT);
  }

  // --- USER (nur im Portal, nicht für SSO nötig) -----------------------------

  setUser(user: User, expiresAtMs?: number): void {
    console.log('[LoginService] setUser called', {
      userEmail: user?.email,
      expiresAtMs,
    });

    this.loginStateService.setUser(user);

    // NICHT im Cookie, sondern im LocalStorage (weil das Objekt riesig ist)
    const json = JSON.stringify(user);
    localStorage.setItem(this.LS_USER, json);
  }

  getUser(): User | null {
    try {
      const raw = localStorage.getItem(this.LS_USER);
      if (!raw) return null;
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  }

  removeUser(): void {
    localStorage.removeItem(this.LS_USER);
    this.loginStateService.setUser(null);
  }

  isAuthenticated(): boolean {
    const token = this.getJWTToken();
    const exp = this.getExpirationToken();
    if (!token) return false;
    if (!exp) return true;
    return Date.now() < exp;
  }

  // --- Backend-Calls ---------------------------------------------------------

  getUserByEmail(email: string): Observable<any> {
    const emailEncrypted = this.encryptionService.encryptPassword(email);
    return this.http.get<any>(
      `${this.endpoint}/getUserByEmail?auth=${this.authentificationKey}&email=${emailEncrypted}`
    );
  }

  /** @param keepSessionOn401 true = ein 401 meldet nicht ab (Übergabe an ein Werkzeug). */
  getAuthParameters(email: string, keepSessionOn401 = false): Observable<any> {
    const emailEncrypted = this.encryptionService.encryptPassword(email);
    return this.http.get<any>(
      `${this.endpoint}/getAuthParametersAlternative?auth=${this.authentificationKey}&email=${emailEncrypted}`,
      { context: new HttpContext().set(KEEP_SESSION_ON_401, keepSessionOn401) }
    );
  }

  // --- Zentraler Logout ------------------------------------------------------

  logout(): void {
    this.removeUser();
    this.removeJWTToken();
    this.removeExpirationToken();
  }
}
